// Global Discount Display Manager (Badges + Prices)
(function() {
    'use strict';
    
    // Detect a discount code from the URL pathname (e.g. /discount/Spring)
    function detectDiscountCodeFromUrl() {
      try {
        const pathMatch = window.location.pathname.match(/\/discount\/([^/?#]+)/i);
        if (pathMatch && pathMatch[1]) {
          return decodeURIComponent(pathMatch[1]);
        }
      } catch (e) {
        // fall through
      }
      return null;
    }
    
    // Read the discount_code cookie (no URL fallback).
    function readDiscountCodeCookie() {
      const cookies = document.cookie.split(';');
      for (let cookie of cookies) {
        const trimmed = cookie.trim();
        const eqIdx = trimmed.indexOf('=');
        if (eqIdx === -1) continue;
        const name = trimmed.substring(0, eqIdx);
        const value = trimmed.substring(eqIdx + 1);
        if (name === 'discount_code' && value) {
          try {
            return decodeURIComponent(value);
          } catch (e) {
            return value;
          }
        }
      }
      return null;
    }
    
    // Read the currently active discount code from URL or cookie
    function getActiveDiscountCode() {
      const urlCode = detectDiscountCodeFromUrl();
      if (urlCode) {
        return urlCode;
      }
      return readDiscountCodeCookie();
    }
    
    // Normalize a code for comparison (case-insensitive, trimmed)
    function normalizeCode(code) {
      if (code === null || code === undefined) return '';
      return String(code).trim().toLowerCase();
    }
    
    // Grace window during which a freshly written cookie is trusted even
    // if Shopify's cart hasn't caught up yet (e.g. the click interceptor
    // wrote the code right before a /discount/<code>?redirect=/path
    // navigation, and the destination page's sync runs before Shopify
    // has reflected the code in cart.discount_codes).
    const COOKIE_FRESHNESS_MS = 5000;
    const DISCOUNT_SET_AT_KEY = 'discount_code_set_at';
    
    // Write the active discount code to the cookie. Resets the 1-hour expiry
    // whenever the code changes (or is being set for the first time) so that
    // switching from /discount/A to /discount/B always starts a fresh timer.
    // Also records a timestamp used by the grace window above.
    function writeDiscountCodeCookie(code) {
      if (!code) return;
      const existing = readDiscountCodeCookie();
      document.cookie = 'discount_code=' + encodeURIComponent(code) + '; path=/;';
      
      const codeChanged = !existing || normalizeCode(existing) !== normalizeCode(code);
      if (codeChanged || !localStorage.getItem('discount_expiry')) {
        const discountExpiry = Date.now() + 60 * 60 * 1000; // 1 hour from now
        localStorage.setItem('discount_expiry', discountExpiry);
      }
      localStorage.setItem(DISCOUNT_SET_AT_KEY, String(Date.now()));
    }
    
    // Returns true if the cookie was written very recently (within the
    // grace window). Used by sync to avoid clearing a code that was just
    // captured by the click interceptor.
    function discountCookieIsFresh() {
      const setAt = Number(localStorage.getItem(DISCOUNT_SET_AT_KEY) || 0);
      return setAt && (Date.now() - setAt) < COOKIE_FRESHNESS_MS;
    }
    
    // Clear the locally stored discount code + expiry.
    function clearDiscountCodeCookie() {
      document.cookie = 'discount_code=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      localStorage.removeItem('discount_expiry');
      localStorage.removeItem(DISCOUNT_SET_AT_KEY);
    }
    
    // Fetch the most recently added discount code currently attached to
    // the Shopify cart. This is the source of truth when the user came
    // in via a URL like /discount/<code>?redirect=/some/path — the
    // /discount/<code> segment is gone by the time JS runs (Shopify
    // 302s before the browser ever lands on it), but Shopify has already
    // attached the code to the cart server-side, so cart.discount_codes
    // still reflects it.
    //
    // We iterate in reverse and pick the LAST code (regardless of
    // applicability). The most recently added code best reflects the
    // user's latest intent: e.g. cart = [PLAY26, WAGONFEST26] from a
    // testing session must resolve to WAGONFEST26, not PLAY26, even if
    // both happen to be "applicable".
    // Every code on the cart at the last fetch, for bf2026ExcludedCodeActive().
    let cartDiscountCodes = [];

    async function fetchAppliedDiscountCode() {
      try {
        const res = await fetch('/cart.js', {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });
        if (!res.ok) return null;
        const cart = await res.json();
        if (!cart || !Array.isArray(cart.discount_codes) || cart.discount_codes.length === 0) {
          cartDiscountCodes = [];
          return null;
        }
        cartDiscountCodes = cart.discount_codes.map(d => d && d.code).filter(Boolean);
        for (let i = cart.discount_codes.length - 1; i >= 0; i--) {
          const d = cart.discount_codes[i];
          if (d && d.code) return d.code;
        }
        return null;
      } catch (e) {
        return null;
      }
    }
    
    // Reconcile the local discount_code cookie with the actual current
    // state. Precedence (highest wins):
    //   1. /discount/<code> in the URL pathname
    //   2. Shopify cart's most recent discount code (handles redirected URLs)
    //   3. A recently-written cookie still inside the freshness grace
    //      window (handles the click-interceptor -> navigation race)
    //   4. Nothing — clear the cookie so a stale code from an earlier
    //      session does not leak into the current page
    // Returns the resolved code (or null if there is none).
    async function syncActiveDiscountCode() {
      const urlCode = detectDiscountCodeFromUrl();
      if (urlCode) {
        writeDiscountCodeCookie(urlCode);
        return urlCode;
      }
      
      const cartCode = await fetchAppliedDiscountCode();
      if (cartCode) {
        writeDiscountCodeCookie(cartCode);
        return cartCode;
      }
      
      if (discountCookieIsFresh()) {
        return readDiscountCodeCookie();
      }
      
      clearDiscountCodeCookie();
      return null;
    }
    
    // Validate that an entry looks like a usable discount object.
    function isValidDiscountEntry(entry) {
      return (
        entry &&
        typeof entry === 'object' &&
        entry.type &&
        entry.value !== undefined
      );
    }
    
    // Returns true when the entry has no required discount code (legacy /
    // catch-all tier). Treats undefined, null, and empty/whitespace strings
    // the same.
    function isCodelessDiscountEntry(entry) {
      const code = entry && entry.discountCode;
      return code === undefined || code === null || String(code).trim() === '';
    }
    
    // Pick the best matching discount from either a single discount object
    // (legacy format) or an array of discount objects (new tiered format).
    //
    // Selection order:
    //   1. Exact match on the currently active discount code
    //      (case-insensitive). Applies to both shapes.
    //   2. For legacy single-object metafields only, fall back to a tier
    //      with no `discountCode` (catch-all). Multi-tier arrays do NOT
    //      get this fallback: if the active code does not match any tier
    //      exactly, the product shows no discount.
    //
    // Rationale: a leftover empty-code tier inside a tiered array must
    // not bleed across discount codes. Switching from /discount/A to
    // /discount/B should hide the discount on products whose array
    // contains only A (or only an empty-code catch-all).
    function selectMatchingDiscount(discountData, activeCode) {
      if (!discountData) return null;
      
      const isArray = Array.isArray(discountData);
      const candidates = isArray ? discountData : [discountData];
      
      if (activeCode) {
        for (const candidate of candidates) {
          if (!isValidDiscountEntry(candidate)) continue;
          if (isCodelessDiscountEntry(candidate)) continue;
          if (normalizeCode(candidate.discountCode) === normalizeCode(activeCode)) {
            return candidate;
          }
        }
      }
      
      if (!isArray) {
        for (const candidate of candidates) {
          if (!isValidDiscountEntry(candidate)) continue;
          if (isCodelessDiscountEntry(candidate)) {
            return candidate;
          }
        }
      }
      
      return null;
    }
    
    // ---- Theme settings > Discount #1-5 tabs ----
    // Each product registers the tabs it matches (snippets/theme-discount-match)
    // as `themeDiscounts`: [{ tab, type, value, variants?, compareAt? }], in tab order.
    // `variants` (from "Specific variants" tabs) lists the only variant IDs the
    // tab applies to.

    // True while a code from Theme settings > Black Friday 2026 > Discount codes
    // (window.bf2026ExcludedCodes, layout/theme.liquid) is in the URL, the
    // discount_code cookie, or anywhere on the cart. Ignores the 1-hour display
    // timer: a code still on the cart still applies at checkout. Those codes
    // get no Black Friday discount tabs and no pill badge.
    function bf2026ExcludedCodeActive() {
      if (!Array.isArray(window.bf2026ExcludedCodes)) return false;
      const excluded = window.bf2026ExcludedCodes.map(normalizeCode).filter(Boolean);
      const applied = [detectDiscountCodeFromUrl(), readDiscountCodeCookie(), ...cartDiscountCodes];
      return applied.some(code => excluded.includes(normalizeCode(code)));
    }

    // Email-only private sale: while Theme settings > Black Friday 2026 >
    // Email-only access page is set (window.bf2026AccessRequired), Black Friday
    // discounts stay locked until that page has been opened in this browser. The
    // page stores an expiry time 48 hours ahead (layout/theme.liquid), shared by
    // every tab. Having an "Email-only access codes" code active (URL, the
    // discount_code cookie or the cart) unlocks it the same way. From "Early
    // access ends" (window.bf2026AccessEndsAt) the lock is off for everyone.
    function bf2026AccessLocked() {
      if (!window.bf2026AccessRequired) return false;
      if (window.bf2026AccessEndsAt && Date.now() >= window.bf2026AccessEndsAt) return false;

      let unlockedUntil = 0;
      try {
        unlockedUntil = Number(localStorage.getItem('bf2026Access'));
      } catch (e) {
        // Storage blocked: only an active access code can unlock
      }
      if (unlockedUntil > Date.now()) return false;

      const accessCodes = (window.bf2026AccessCodes || []).map(normalizeCode).filter(Boolean);
      const applied = [detectDiscountCodeFromUrl(), readDiscountCodeCookie(), ...cartDiscountCodes];
      if (!applied.some(code => accessCodes.includes(normalizeCode(code)))) return true;

      try {
        localStorage.setItem('bf2026Access', String(Date.now() + 48 * 60 * 60 * 1000));
      } catch (e) {
        // Storage blocked: the active code still unlocks this page
      }
      return false;
    }

    // Black Friday 2026 mobile PDP text ('tagText' or 'endsText') for a matched
    // discount: the global Theme settings > Black Friday 2026 text when set
    // (window.bf2026Texts, layout/theme.liquid), else the discount tab's own.
    function bf2026Text(discountInfo, key) {
      if (!window.bf2026Texts || bf2026ExcludedCodeActive() || bf2026AccessLocked()) return '';
      return window.bf2026Texts[key] || (discountInfo && discountInfo[key]) || '';
    }

    // First (lowest-numbered) usable tab for the variant, or null.
    function selectThemeDiscount(themeDiscounts, variantId) {
      if (!Array.isArray(themeDiscounts) || bf2026ExcludedCodeActive() || bf2026AccessLocked()) return null;
      for (const entry of themeDiscounts) {
        if (!isValidDiscountEntry(entry) || Number(entry.value) <= 0) continue;
        if (Array.isArray(entry.variants) && !entry.variants.some(id => String(id) === String(variantId))) continue;
        return entry;
      }
      return null;
    }

    // Function to check if discount code is valid.
    // Assumes the cookie has already been reconciled with the URL and Shopify
    // cart via syncActiveDiscountCode(). Only enforces the 1-hour expiry timer.
    async function isDiscountCodeValid() {
      try {
        const cookieCode = readDiscountCodeCookie();
        if (!cookieCode) return false;
        
        const storedExpiry = localStorage.getItem('discount_expiry');
        if (!storedExpiry) {
          const discountExpiry = Date.now() + 60 * 60 * 1000; // 1 hour from now
          localStorage.setItem('discount_expiry', discountExpiry);
          return true;
        }
        
        if (Date.now() < Number(storedExpiry)) {
          return true;
        }
        
        clearDiscountCodeCookie();
        return false;
      } catch (error) {
        return false;
      }
    }
    
    // Function to get selected variant ID
    function getSelectedVariantId(productId) {
      // Try URL parameter first
      const urlParams = new URLSearchParams(window.location.search);
      const variantParam = urlParams.get('variant');
      if (variantParam) {
        return variantParam;
      }
      
      // Try form selectors
      const selectors = [
        `form[data-product-id="${productId}"] input[name="id"]:checked`,
        `form[data-product-id="${productId}"] input[name="id"]`,
        `form[data-product-id="${productId}"] select[name="id"]`,
        'input[name="id"]:checked',
        'input[name="id"]',
        'select[name="id"]'
      ];
      
      for (const selector of selectors) {
        const element = document.querySelector(selector);
        if (element) {
          const variantId = element.value || element.getAttribute('data-variant-id');
          if (variantId) {
            return variantId;
          }
        }
      }
      
      return null;
    }
    
    // Variant picked with a collection card's swatches (global.js VariantSelects
    // keeps it in the card's data-selected-variant), or null.
    function getCardVariantId(displayId) {
      const card = document.getElementById(displayId)?.closest('.product-card');
      return (card && card.dataset.selectedVariant) || null;
    }

    // Collection cards render the default variant's price. Point the card's
    // dynamic price at the selected variant so regular and sale prices match it.
    function setCardVariantPrice(displayId, cents) {
      const card = document.getElementById(displayId)?.closest('.product-card');
      if (!card || !Number.isFinite(cents)) return;
      card.querySelectorAll('.dynamic-price-wrapper [data-original-price]').forEach(el => {
        el.setAttribute('data-original-price', String(cents));
      });
      card.querySelectorAll('.dynamic-price-wrapper .price-no-discount > [data-original-price]').forEach(el => {
        el.textContent = formatMoney(cents);
      });
    }

    // Format a cents value into a money string using Shopify.formatMoney when
    // available; falls back to a simple `$X.XX` representation.
    function formatMoney(cents) {
      const numericCents = Number(cents);
      if (!Number.isFinite(numericCents)) return '';
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return '$' + window.Shopify.formatMoney(numericCents, '{{amount}}');
      }
      return '$' + (numericCents / 100).toFixed(2);
    }

    // Discount value for display: cut (not rounded) to two decimals, trailing zeros dropped
    // (14.2857 → "14.28", 20 → "20"); matches snippets/discount-value-label.liquid.
    function formatDiscountValue(value) {
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) return String(value);
      return String(Math.floor(numericValue * 100 + 1e-6) / 100);
    }

    // Black Friday 2026 pill text: "20% OFF" or "$20 OFF"
    function discountOffLabel(discountInfo) {
      const offValue = formatDiscountValue(discountInfo.value);
      return discountInfo.type === 'dollar' ? `$${offValue} OFF` : `${offValue}% OFF`;
    }
    
    // The Black Friday 2026 pill stays hidden while an excluded code is active or the private sale is locked
    function bf2026PillAllowed() {
      return !bf2026ExcludedCodeActive() && !bf2026AccessLocked();
    }

    // Compute the discounted price (in cents) for the given original price
    // and a discount info object ({ type: 'percentage' | 'dollar', value }).
    function computeDiscountedCents(originalCents, discountInfo) {
      if (!Number.isFinite(originalCents) || !discountInfo) return originalCents;
      
      const value = Number(discountInfo.value);
      if (!Number.isFinite(value) || value <= 0) return originalCents;
      
      let discounted = originalCents;
      if (discountInfo.type === 'percentage') {
        const off = Math.round((originalCents * value) / 100);
        discounted = originalCents - off;
      } else if (discountInfo.type === 'dollar') {
        const offCents = Math.round(value * 100);
        discounted = originalCents - offCents;
      } else {
        // Fallback: treat unknown types as percentage (mirrors dynamic-price.liquid)
        const off = Math.round((originalCents * value) / 100);
        discounted = originalCents - off;
      }
      
      return Math.max(0, discounted);
    }

    // Tabs set to "Show discount off compare-at price" ("compareAt") measure the
    // sale from the variant's compare-at price: the struck price, the OFF text
    // and the savings. The sale price itself is unchanged. Null when the tab
    // doesn't ask for it or the variant has no compare-at above its price.
    function compareAtDiscountOff(originalCents, discountInfo) {
      const compareCents = Number(discountInfo && discountInfo.compareAtCents);
      if (!discountInfo || !discountInfo.compareAt || !(compareCents > originalCents)) return null;

      const saveCents = compareCents - computeDiscountedCents(originalCents, discountInfo);
      // Whole percent, rounded down so the label never overstates the saving
      const text = discountInfo.type === 'dollar'
        ? `$${formatDiscountValue(saveCents / 100)} OFF`
        : `${Math.floor((saveCents * 100) / compareCents)}% OFF`;
      return { compareCents, saveCents, text };
    }

    // A variant in any matching tab set to "Show discount off compare-at price"
    // shows whichever discount wins (code tier or tab) off its compare-at price,
    // so a lower-numbered tab matching the same product doesn't undo it. The
    // badge has no price wrapper to read, so the variant's prices ride along.
    function withCompareAtBasis(discountInfo, displayData, variantId) {
      const { themeDiscounts, variantPrices, variantComparePrices, firstVariantId } = displayData;
      if (!Array.isArray(themeDiscounts) || !variantPrices || !variantComparePrices) return discountInfo;

      const priceVariantId = variantId || firstVariantId;
      const compareAt = themeDiscounts.some(entry => entry.compareAt
        && (!Array.isArray(entry.variants) || entry.variants.some(id => String(id) === String(priceVariantId))));
      if (!compareAt) return discountInfo;

      return {
        ...discountInfo,
        compareAt: true,
        priceCents: Number(variantPrices[priceVariantId]),
        compareAtCents: Number(variantComparePrices[priceVariantId])
      };
    }

    // Re-render the discounted price inside `.price-with-discount` based on
    // the matched tier. The liquid snippet bakes in only the first tier's
    // value when the metafield is an array, so we must overwrite here.
    function applyDiscountToPriceWrapper(priceWrapper, discountInfo) {
      if (!priceWrapper || !discountInfo) return;

      // "Sale ends text"; the mobile buy box shows it above Add to Cart
      priceWrapper.setAttribute('data-ends-text', bf2026Text(discountInfo, 'endsText'));

      const originalEl = priceWrapper.querySelector('[data-original-price]');
      const originalCents = originalEl ? Number(originalEl.getAttribute('data-original-price')) : NaN;
      if (!Number.isFinite(originalCents)) return;
      
      const discountedCents = computeDiscountedCents(originalCents, discountInfo);
      const discountedMoney = formatMoney(discountedCents);
      const originalMoney = formatMoney(originalCents);
      const compareAtOff = compareAtDiscountOff(originalCents, discountInfo);

      const discountedEl = priceWrapper.querySelector('.price-with-discount .price-discounted');
      if (discountedEl) {
        discountedEl.setAttribute('data-discounted-price', String(discountedCents));
        discountedEl.textContent = discountedMoney;
      }

      const compareEl = priceWrapper.querySelector('.price-with-discount .price-compare');
      if (compareEl) {
        compareEl.setAttribute('data-original-price', String(originalCents));
        compareEl.textContent = compareAtOff ? formatMoney(compareAtOff.compareCents) : originalMoney;
      }

      const percentEl = priceWrapper.querySelector('.price-with-discount .price-discounted-percentage');
      if (percentEl) {
        if (compareAtOff) {
          percentEl.textContent = `(${compareAtOff.text})`;
        } else if (discountInfo.type === 'dollar') {
          percentEl.textContent = `($${formatDiscountValue(discountInfo.value)} OFF)`;
        } else {
          percentEl.textContent = `(${formatDiscountValue(discountInfo.value)}% OFF)`;
        }
      }

      // PDP pill badge: "20% OFF • Save $140.00" (savings to two decimal places)
      const pillRow = priceWrapper.querySelector('.price-discount-pill-row');
      if (pillRow) pillRow.hidden = !bf2026PillAllowed();
      const pillOffEl = priceWrapper.querySelector('.price-discount-pill__off');
      if (pillOffEl) pillOffEl.textContent = compareAtOff ? compareAtOff.text : discountOffLabel(discountInfo);
      const pillSaveEl = priceWrapper.querySelector('.price-discount-pill__save');
      if (pillSaveEl) {
        const saveCents = compareAtOff ? compareAtOff.saveCents : originalCents - discountedCents;
        pillSaveEl.textContent = `Save ${formatMoney(saveCents)}`;
        pillSaveEl.hidden = saveCents < 1;
      }
    }
    
    // Function to update price display in the same container as the badge
    function updatePriceInSameContainer(badgeElement, showDiscount, calloutText = 'sale ends Nov 30th', discountInfo = null) {
      if (!badgeElement) return;
      
      // Find the parent container (could be product card, product section, etc.)
      const container = badgeElement.closest('.product, .product-card, .page-width, section');
      
      if (container) {
        // Find ALL price wrappers in the same container (mobile + desktop)
        const priceWrappers = container.querySelectorAll('.dynamic-price-wrapper');
        
        if (priceWrappers.length > 0) {
          priceWrappers.forEach(priceWrapper => {
            const productCardContent = priceWrapper.closest('.product-card__content');
            const mobileHeaderPrice = priceWrapper.closest('.product__mobile-header-price');
            const reviewsPrice = priceWrapper.closest('.reviews__price');
            const strollerPrice = priceWrapper.closest('.stroller-main-product__price');
            const stickyBarPrice = priceWrapper.closest('.cro-pdp-sticky-bar__price');
            
            // Sticky bar mirrors PDP price via JS sync — update values only, never inject callout UI
            if (stickyBarPrice) {
              if (showDiscount) {
                priceWrapper.classList.add('discount-active');
                applyDiscountToPriceWrapper(priceWrapper, discountInfo);
                const noDiscount = priceWrapper.querySelector('.price-no-discount, .price-regular');
                const withDiscount = priceWrapper.querySelector('.price-with-discount');
                if (noDiscount) noDiscount.style.display = 'none';
                if (withDiscount) withDiscount.style.display = 'inline-flex';
              } else {
                priceWrapper.classList.remove('discount-active');
                const noDiscount = priceWrapper.querySelector('.price-no-discount, .price-regular');
                const withDiscount = priceWrapper.querySelector('.price-with-discount');
                if (noDiscount) noDiscount.style.display = 'inline';
                if (withDiscount) withDiscount.style.display = 'none';
              }
              return;
            }

            const isPDPContainer = mobileHeaderPrice || reviewsPrice || strollerPrice;
            const targetContainer = productCardContent || mobileHeaderPrice || reviewsPrice || strollerPrice;
            
            if (!targetContainer) return;
            
            const priceDiscountCallout = document.createElement('div');
            priceDiscountCallout.classList.add('price-with-discount__callout');
            priceDiscountCallout.innerHTML = `<span>${calloutText}</span>`;

            if (showDiscount) {
              priceWrapper.classList.add('discount-active');
              
              // Re-render the discounted price for the matched tier. Required
              // because the liquid snippet may have rendered a different tier.
              applyDiscountToPriceWrapper(priceWrapper, discountInfo);
              
              // Also force inline styles for certainty
              const noDiscount = priceWrapper.querySelector('.price-no-discount, .price-regular');
              const withDiscount = priceWrapper.querySelector('.price-with-discount');
              if (noDiscount) noDiscount.style.display = 'none';
              if (withDiscount) withDiscount.style.display = 'inline-flex';

              if (isPDPContainer) {
                if (!targetContainer.nextElementSibling || !targetContainer.nextElementSibling.classList.contains('price-with-discount__callout')) {
                  targetContainer.insertAdjacentElement('afterend', priceDiscountCallout);
                }
              } else {
                if (!targetContainer.querySelector('.price-with-discount__callout')) {
                  targetContainer.append(priceDiscountCallout);
                }
              }
            } else {
              priceWrapper.classList.remove('discount-active');
              
              if (isPDPContainer) {
                const nextSibling = targetContainer.nextElementSibling;
                if (nextSibling && nextSibling.classList.contains('price-with-discount__callout')) {
                  nextSibling.remove();
                }
              } else {
                targetContainer.querySelector('.price-with-discount__callout')?.remove();
              }
              
              // Also force inline styles for certainty
              const noDiscount = priceWrapper.querySelector('.price-no-discount, .price-regular');
              const withDiscount = priceWrapper.querySelector('.price-with-discount');
              if (noDiscount) noDiscount.style.display = 'inline';
              if (withDiscount) withDiscount.style.display = 'none';
            }
          });
        }
      }
    }
    
    // Function to show discount
    function showDiscount(displayId, discountInfo, source, badgeStyles, calloutText = 'sale ends Nov 30th') {
      const discountDisplay = document.getElementById(displayId);
      if (!discountDisplay) return;
      
      const discountType = discountInfo.type;
      const discountValue = formatDiscountValue(discountInfo.value);
      
      const compareAtOff = compareAtDiscountOff(Number(discountInfo.priceCents), discountInfo);
      let discountText = '';
      if (compareAtOff) {
        discountText = compareAtOff.text;
      } else if (discountType === 'percentage') {
        discountText = `${discountValue}% OFF`;
      } else {
        discountText = `$${discountValue} OFF`;
      }
      
      // Use badge styles from theme settings
      discountDisplay.innerHTML = `<span class="discount-badge" style="${badgeStyles}">${discountText}</span>`;

      // "Image tag text": product page and product card tags read "20% OFF"
      // over the text (styled for mobile in black-friday-2026.css).
      const tagText = bf2026Text(discountInfo, 'tagText');
      if (tagText) {
        const badge = discountDisplay.querySelector('.discount-badge');
        badge.innerHTML = '';
        const valueEl = document.createElement('span');
        valueEl.className = 'discount-badge__value';
        valueEl.textContent = discountText;
        const labelEl = document.createElement('span');
        labelEl.className = 'discount-badge__label';
        labelEl.textContent = tagText;
        badge.append(valueEl, labelEl);
      }
      discountDisplay.classList.toggle('discount-callout--tag', !!tagText);
      discountDisplay.style.display = 'block';
      
      // Update price display in the same container (pass the matched discount
      // info so the price text can be re-rendered for the correct tier).
      updatePriceInSameContainer(discountDisplay, true, calloutText, discountInfo);
    }
    
    // Function to hide discount
    function hideDiscount(displayId) {
      const discountDisplay = document.getElementById(displayId);
      if (discountDisplay) {
        discountDisplay.style.display = 'none';
        discountDisplay.classList.remove('discount-callout--tag');
        
        // Update price display in the same container
        updatePriceInSameContainer(discountDisplay, false);
      }
    }
    
    // Function to update a single discount display.
    // An active discount code's tier wins; otherwise the first live Theme
    // settings discount tab applies. `themeOnly` skips the code check, for the
    // first paint before the code has been reconciled with the cart.
    async function updateDiscountDisplay(displayData, themeOnly = false) {
      const { productId, displayId, context, hasMultipleVariants, variantMetafieldsMap, productDiscount, themeDiscounts, badgeStyles, firstVariantId, variantPrices, calloutText } = displayData;

      // Resolve which variant to check based on context.
      // PRODUCT PAGE: use the currently selected variant.
      // COLLECTION PAGE: use the card's swatch-selected variant (default variant
      // until a swatch is picked).
      let targetVariantId;
      if (context === 'product') {
        targetVariantId = getSelectedVariantId(productId);
      } else {
        targetVariantId = getCardVariantId(displayId) || firstVariantId;
        if (variantPrices) setCardVariantPrice(displayId, Number(variantPrices[targetVariantId]));
      }

      // Check if discount code is valid
      const hasValidDiscount = !themeOnly && await isDiscountCodeValid();
      if (hasValidDiscount) {
        // Resolve the currently active discount code (from URL or cookie).
        // Used to gate which discounts are shown to the customer.
        const activeCode = getActiveDiscountCode();

        // Check variant-level discount first (PRIORITY).
        // Both legacy (single object) and new (array of tiered discounts) shapes
        // are supported via selectMatchingDiscount.
        if (targetVariantId && variantMetafieldsMap[targetVariantId]) {
          const matchedVariantDiscount = selectMatchingDiscount(
            variantMetafieldsMap[targetVariantId],
            activeCode
          );
          if (matchedVariantDiscount) {
            showDiscount(displayId, withCompareAtBasis(matchedVariantDiscount, displayData, targetVariantId), 'variant', badgeStyles, calloutText);
            return;
          }
        }

        // Fall back to product-level discount.
        const matchedProductDiscount = selectMatchingDiscount(productDiscount, activeCode);
        if (matchedProductDiscount) {
          showDiscount(displayId, withCompareAtBasis(matchedProductDiscount, displayData, targetVariantId), 'product', badgeStyles, calloutText);
          return;
        }
      }

      // Fall back to the Theme settings discount tabs.
      const matchedThemeDiscount = selectThemeDiscount(themeDiscounts, targetVariantId);
      if (matchedThemeDiscount) {
        showDiscount(displayId, withCompareAtBasis(matchedThemeDiscount, displayData, targetVariantId), 'theme', badgeStyles, calloutText);
        return;
      }

      // No matching discount found
      hideDiscount(displayId);
    }

    // Cart lines (snippets/cart-item.liquid) that Shopify hasn't discounted carry
    // their matching tabs as data-theme-discounts. Swap in the tab's sale price
    // when one is live. Marks each line so the cart observer skips it next time.
    // Subtotals carrying data-theme-discount-subtotal (Shopify's total in cents)
    // drop by the same savings so they match the lines above them.
    function updateCartThemeDiscounts() {
      // Keyed by line: on /cart the drawer and the page render the same lines.
      const savingsByLine = {};

      document.querySelectorAll('.cart-item__theme-discount').forEach(line => {
        line.setAttribute('data-theme-discount-ready', '');

        let entries = null;
        try {
          entries = JSON.parse(line.getAttribute('data-theme-discounts'));
        } catch (e) {
          // Leave entries null: the regular price stays visible
        }

        const matched = selectThemeDiscount(entries, line.getAttribute('data-variant-id'));
        const originalCents = Number(line.getAttribute('data-original-price'));
        const regularPrice = line.parentElement && line.parentElement.querySelector('.updated--price');
        const salePrice = line.querySelector('[data-theme-discount-price]');

        if (matched && Number.isFinite(originalCents) && salePrice) {
          const discountedCents = computeDiscountedCents(originalCents, matched);
          salePrice.textContent = formatMoney(discountedCents);
          line.style.display = '';
          if (regularPrice) regularPrice.style.display = 'none';

          const quantity = Number(line.getAttribute('data-quantity')) || 0;
          const lineKey = line.getAttribute('data-line-key');
          savingsByLine[lineKey] = (originalCents - discountedCents) * quantity;
        } else {
          line.style.display = 'none';
          if (regularPrice) regularPrice.style.display = '';
        }
      });

      const savings = Object.keys(savingsByLine).reduce((sum, key) => sum + savingsByLine[key], 0);
      document.querySelectorAll('[data-theme-discount-subtotal]').forEach(subtotal => {
        const totalCents = Number(subtotal.getAttribute('data-theme-discount-subtotal'));
        if (!Number.isFinite(totalCents)) return;
        subtotal.textContent = formatMoney(Math.max(0, totalCents - savings));
      });
    }

    // Function to update all discount displays
    async function updateAllDiscountDisplays(themeOnly = false) {
      updateCartThemeDiscounts();

      if (!window.discountDisplays || window.discountDisplays.length === 0) {
        return;
      }

      for (const displayData of window.discountDisplays) {
        await updateDiscountDisplay(displayData, themeOnly === true);
      }

      // Let other components (e.g. quick view) re-render their prices against
      // the freshly reconciled discount state.
      document.dispatchEvent(new CustomEvent('discounts:updated'));
    }
    
    // Reconcile the active discount code with Shopify, then re-render all
    // discount displays. Use this whenever the source of truth could have
    // changed (initial load, URL change, cart update).
    async function syncAndUpdateAllDisplays() {
      await syncActiveDiscountCode();
      await updateAllDiscountDisplays();
    }
    
    // Initialize on page load
    function init() {
      // Paint theme discount tabs right away. Code tiers need the cart sync
      // below, so they follow.
      updateAllDiscountDisplays(true);

      // Cart drawer/page sections are re-rendered by replacing their HTML, which
      // drops the swapped-in sale prices. Re-apply them to any new cart lines.
      let cartRefreshQueued = false;
      new MutationObserver(() => {
        if (cartRefreshQueued) return;
        cartRefreshQueued = true;
        requestAnimationFrame(() => {
          cartRefreshQueued = false;
          if (document.querySelector('.cart-item__theme-discount:not([data-theme-discount-ready])')) {
            updateCartThemeDiscounts();
          }
        });
      }).observe(document.body, { childList: true, subtree: true });

      // Initial sync + render. The sync step is what catches redirected
      // /discount/<code>?redirect=/path URLs by reading the applied code
      // from the Shopify cart.
      setTimeout(() => {
        syncAndUpdateAllDisplays();
      }, 500);
      
      // Click interceptor: if the user clicks any <a> whose href points
      // at /discount/<code>, capture the code into the cookie BEFORE the
      // navigation happens. This is the only reliable way to know the
      // code when Shopify is about to 302-redirect a URL like
      // /discount/CODE?redirect=/path — by the time the destination
      // page's JS runs, the /discount/CODE segment is gone, so we'd
      // otherwise be left with a stale cookie or a cart that may not
      // yet reflect the new code (e.g. empty cart, mixed code history).
      // Runs in the capture phase so it executes before any framework
      // click handlers that might preventDefault.
      document.addEventListener('click', function(e) {
        const link = e.target && e.target.closest && e.target.closest('a[href*="/discount/"]');
        if (!link) return;
        const href = link.getAttribute('href') || '';
        const match = href.match(/\/discount\/([^/?#]+)/i);
        if (!match) return;
        try {
          writeDiscountCodeCookie(decodeURIComponent(match[1]));
        } catch (_) {
          writeDiscountCodeCookie(match[1]);
        }
      }, true);
      
      // Listen for variant changes (product pages only). No re-sync needed:
      // changing variants does not change the active discount code.
      document.addEventListener('change', function(e) {
        // Card swatches bubble here after VariantSelects has updated the card.
        if (e.target && (e.target.name === 'id' || e.target.classList.contains('variant-input') || e.target.closest('.product-card variant-radios'))) {
          updateAllDiscountDisplays();
        }
      });
      // Swatch pickers (component-color-variant-selection) announce the new
      // variant here instead; needed for "Specific variants" theme tabs.
      document.addEventListener('variant:change', () => {
        updateAllDiscountDisplays();
      });

      // Listen for cart updates - resync because the applied discount code
      // can change when items are added/removed or a code is entered.
      document.addEventListener('cart:updated', () => {
        syncAndUpdateAllDisplays();
      });
      
      // Listen for URL changes - resync because the user may have navigated
      // to a new /discount/<code> URL (or to a page that triggered a new
      // Shopify discount via redirect).
      let currentUrl = window.location.href;
      setInterval(() => {
        if (window.location.href !== currentUrl) {
          currentUrl = window.location.href;
          syncAndUpdateAllDisplays();
        }
      }, 500);

      // Periodic check for timer expiration (every 30 seconds)
      setInterval(() => {
        const expiry = localStorage.getItem('discount_expiry');
        if (expiry && Date.now() >= Number(expiry)) {
          // Timer expired, update displays to hide discounts
          updateAllDiscountDisplays();
        }
      }, 30000); // Check every 30 seconds
    }
    
    // Run when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
    
    // Expose global function for manual updates
    window.updateDiscountDisplays = updateAllDiscountDisplays;
    
    // Read-only, synchronous version of isDiscountCodeValid (no cookie writes,
    // no expiry side effects). Safe for other components to call at render time.
    function isDiscountCodeValidSync() {
      const cookieCode = readDiscountCodeCookie();
      if (!cookieCode) return false;
      const storedExpiry = localStorage.getItem('discount_expiry');
      if (storedExpiry && Date.now() >= Number(storedExpiry)) return false;
      return true;
    }
    
    // Resolve the discount tier that currently applies to a variant of a
    // product, or null when there is no valid active code / no matching tier.
    // Uses the same variant-first-then-product precedence and tier matching
    // rules as the badge/price logic above. Descriptor data comes from the
    // discount-badge snippet registrations in window.discountDisplays, or from
    // `fallbackDescriptor` (same shape) for a product with no badge on the page.
    function resolveDiscountForVariant(productId, variantId, fallbackDescriptor) {
      const descriptor = (window.discountDisplays || []).find(
        d => String(d.productId) === String(productId)
      ) || fallbackDescriptor;
      if (!descriptor) return null;

      if (isDiscountCodeValidSync()) {
        const activeCode = getActiveDiscountCode();

        if (variantId != null && descriptor.variantMetafieldsMap) {
          const variantData = descriptor.variantMetafieldsMap[variantId];
          if (variantData) {
            const variantMatch = selectMatchingDiscount(variantData, activeCode);
            if (variantMatch) return variantMatch;
          }
        }

        const productMatch = selectMatchingDiscount(descriptor.productDiscount, activeCode);
        if (productMatch) return productMatch;
      }

      return selectThemeDiscount(descriptor.themeDiscounts, variantId);
    }
    
    // Public read-only API for other components (e.g. component-quick-view.js)
    // so discount gating/parsing stays centralized in this file.
    window.DiscountDisplayAPI = {
      resolveDiscountForVariant: resolveDiscountForVariant,
      computeDiscountedCents: computeDiscountedCents,
      formatMoney: formatMoney,
      discountOffLabel: discountOffLabel,
      bf2026PillAllowed: bf2026PillAllowed
    };
    
    // Debug helper: dumps everything that influences the active discount
    // decision. Run `await window.__discountDebug()` in the browser console
    // to see what state the badge logic is actually working with.
    window.__discountDebug = async function() {
      const allCookies = document.cookie.split(';').map(c => c.trim()).filter(Boolean);
      let cart = null;
      let cartError = null;
      try {
        const res = await fetch('/cart.js', { credentials: 'same-origin' });
        cart = await res.json();
      } catch (e) {
        cartError = String(e);
      }
      
      const activeCode = getActiveDiscountCode();
      const perDisplay = (window.discountDisplays || []).map(d => {
        const targetVariantId = d.context === 'product'
          ? getSelectedVariantId(d.productId)
          : getCardVariantId(d.displayId) || d.firstVariantId;
        const variantData = targetVariantId ? d.variantMetafieldsMap[targetVariantId] : null;
        const variantMatch = selectMatchingDiscount(variantData, activeCode);
        const productMatch = selectMatchingDiscount(d.productDiscount, activeCode);
        const themeMatch = selectThemeDiscount(d.themeDiscounts, targetVariantId);
        const displayEl = document.getElementById(d.displayId);
        return {
          productId: d.productId,
          context: d.context,
          displayId: d.displayId,
          targetVariantId: targetVariantId,
          variantData: variantData,
          productDiscount: d.productDiscount,
          variantMatch: variantMatch,
          productMatch: productMatch,
          themeDiscounts: d.themeDiscounts,
          themeMatch: themeMatch,
          resolved: variantMatch || productMatch || themeMatch || null,
          domStyleDisplay: displayEl ? displayEl.style.display : null,
          domBadgeText: displayEl ? displayEl.textContent.trim() : null
        };
      });
      
      const info = {
        url: window.location.href,
        pathname: window.location.pathname,
        detectedFromUrl: detectDiscountCodeFromUrl(),
        activeCode: activeCode,
        cookieValue: readDiscountCodeCookie(),
        cookieIsFresh: discountCookieIsFresh(),
        localStorage: {
          discount_expiry: localStorage.getItem('discount_expiry'),
          discount_code_set_at: localStorage.getItem(DISCOUNT_SET_AT_KEY)
        },
        cartItemCount: cart && cart.item_count,
        cartDiscountCodes: cart && cart.discount_codes,
        cartError: cartError,
        bf2026ExcludedCodes: window.bf2026ExcludedCodes,
        bf2026ExcludedCodeActive: bf2026ExcludedCodeActive(),
        bf2026AccessRequired: !!window.bf2026AccessRequired,
        bf2026AccessEndsAt: window.bf2026AccessEndsAt ? new Date(window.bf2026AccessEndsAt).toString() : null,
        bf2026AccessLocked: bf2026AccessLocked(),
        allCookieNames: allCookies.map(c => c.split('=')[0]),
        displays: perDisplay
      };
      console.log('[DISCOUNT DEBUG]', info);
      console.table(perDisplay.map(p => ({
        productId: p.productId,
        context: p.context,
        resolved: p.resolved ? `${p.resolved.type} ${p.resolved.value} (${p.resolved.discountCode || 'codeless'})` : 'none',
        badgeVisible: p.domStyleDisplay !== 'none',
        badgeText: p.domBadgeText
      })));
      return info;
    };
    
    // Reset helper for testing. Clears the local discount cookie + storage
    // AND removes all discount codes from the Shopify cart, so the next
    // page load starts from a clean slate. Run `await window.__discountReset()`
    // in the console between test runs.
    window.__discountReset = async function() {
      clearDiscountCodeCookie();
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: '' })
        });
      } catch (e) {
        console.warn('[DISCOUNT RESET] failed to clear cart discount', e);
      }
      console.log('[DISCOUNT RESET] cleared local + cart discount state. Reload the page.');
    };
    
  })();